import React, { Dispatch, SetStateAction, useState } from 'react';
import Modal from 'react-native-modal';
import {
  View, Text, StyleSheet, ActivityIndicator,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import Style from '../../../../../constants/Style';
import Colors from '../../../../../constants/Colors';
import CustomTextField from '../../../../../components/CustomTextField';
import Button from '../../../../../components/Button';
import addAssetNote from '../../../../../requests/Assets/AddAssetNote';
import { AssetNote } from '../../../../../constants/Types';
import editAssetNote from '../../../../../requests/Assets/EditAssetNote';
import PressableStyled from '../../../../../components/PressableStyled';
import BinIcon12 from '../../../../../components/12pxIcons/BinIcon12';
import deleteAssetNote from '../../../../../requests/Assets/DeleteAssetNote';
import CloseModalIcon from '../../../../../components/CloseModalIcon';
import isMobile from '../../../../../components/IsMobile';
import SelectionModal from '../../../../../components/SelectionModal';
import SuccessMessage from '../../../../../components/SuccessMessage';

interface UpdateNoteModalProps {
  isVisible: boolean,
  hide: () => void,
  assetId: string,
  addNote?,
  note?: AssetNote,
  editNote?,
  deleteNote?,
  showScreenAlert,
  noteViewMode?: boolean,
  setNoteViewMode?: Dispatch<SetStateAction<boolean>>,
}

const UpdateNoteModal = ({
  hide, isVisible, assetId, addNote, showScreenAlert, note, editNote, noteViewMode, setNoteViewMode,
  deleteNote,
}: UpdateNoteModalProps) => {
  const { firstName, lastName } = useSelector((state:
    { user: { firstName, lastName } }) => state.user);
  const username: string = `${firstName ?? ''} ${lastName ?? ''}`;
  const [title, setTitle] = useState<string>(note?.title ?? '');
  const [description, setDescription] = useState<string>(note?.description ?? '');
  const [loading, setLoading] = useState<boolean>(false);
  const [actionComplete, setActionComplete] = useState<boolean>(false);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const showContent: boolean = !!note || !!addNote;

  const restoreDefaults: () => void = () => {
    setTitle(note?.title ?? '');
    setDescription(note?.description ?? '');
    setActionComplete(false);
    setDeleted(false);
    setErrorMessage('');
  };

  const disabled: boolean = !(title?.length > 0);

  const update: () => void = () => {
    if (noteViewMode) {
      setNoteViewMode(false);
      return;
    }
    setLoading(true);
    setErrorMessage('');
    const payload = {
      Title: title,
      Description: description ?? '',
      name_value: username,
      Id: note?.id ?? '',
    };
    (editNote ? editAssetNote(assetId, payload) : addAssetNote(assetId, payload))
      .then((response) => {
        const newNote: AssetNote = {
          title,
          description,
          name: username,
          id: response.NoteId ?? note?.id ?? '',
          createdOn: Moment().toISOString(),
          updatedOn: Moment().toISOString(),
        };
        if (editNote) editNote(newNote);
        else addNote(newNote);
        if (isMobile) setActionComplete(true);
        else {
          showScreenAlert(
            editNote ? 'Edited note' : 'Added note',
            editNote ? 'Note has been edited successfully' : 'Note has been added successfully',
            false,
          );
          restoreDefaults();
          hide();
        }
      }).catch(() => {
        if (isMobile) {
          setErrorMessage(`Unable to ${editNote ? 'edit' : 'add'} note at the moment`);
        } else {
          showScreenAlert('Error', `Unable to ${editNote ? 'edit' : 'add'} note at the moment`);
        }
      }).finally(() => setLoading(false));
  };

  const attemptNoteDeletion: () => void = () => {
    setLoading(true);
    setErrorMessage('');
    deleteAssetNote(assetId, note.id).then(() => {
      deleteNote(note.id);
      if (isMobile) setDeleted(true);
      else {
        hide();
        showScreenAlert('Deleted note', 'Note has been deleted successfully', false);
      }
    }).catch(() => {
      if (isMobile) setErrorMessage('Unable to delete note at the moment');
      else showScreenAlert('Error', 'Unable to delete note at the moment');
    }).finally(() => setLoading(false));
  };

  const updatedTime: string = note?.updatedOn ? new Date(note.updatedOn).toLocaleString() : null;
  const createdTime: string = note?.createdOn ? new Date(note.createdOn).toLocaleString() : null;
  let buttonTitle: string = editNote ? 'Save' : 'Add';
  if (noteViewMode) buttonTitle = 'Edit';

  const Content = () => ((actionComplete || deleted) ? <View style={{ flex: 1 }}>
    <SuccessMessage
      title={deleted ? 'Note deleted successfully'
        : `Note ${editNote ? 'edited' : 'added'} successfully`}
      style={styles.successMessage}
    />
  </View> : <>
    {noteViewMode ? <>
      {note.description && <Text style={styles.description}>{note.description}</Text>}
      <Text style={styles.signature}>{`${updatedTime ?? createdTime} by ${note.name}`}</Text>
    </> : <>
      <CustomTextField
        required
        title='Name'
        value={title}
        updateValue={setTitle}
        testID='note-modal-name-input-field'
      />
      <CustomTextField
        multiline
        maxLength={150}
        indicateLength
        title='Description'
        value={description}
        updateValue={setDescription}
        style={styles.descriptionField}
        testID='note-modal-desc-input-field'
      />
      {errorMessage?.length > 0 && <Text style={styles.errorMessage}>{errorMessage}</Text>}
      {isMobile && <View style={styles.buttonContainer}>
        {!!editNote && <PressableStyled
          style={styles.deleteButton}
          onPress={attemptNoteDeletion}
        >
          <BinIcon12 color={styles.deleteButton.borderColor} />
        </PressableStyled>}
        <Button
          affirmative
          title={buttonTitle}
          onPress={update}
          style={styles.button}
          {...{ disabled }}
        />
      </View>}
    </>}
  </>);

  return isMobile ? <SelectionModal
    {...{
      isVisible, loading, buttonTitle, hide,
    }}
    listContent={Content()}
    title={editNote ? 'Edit note' : 'Add a note'}
    scrollEnabled={false}
    style={styles.mobileContainer}
    onModalHide={restoreDefaults}
    onModalWillShow={restoreDefaults}
  /> : <Modal
    isVisible={isVisible && showContent}
    onSwipeComplete={hide}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    style={Style.modal}
    onModalHide={restoreDefaults}
    onModalWillShow={restoreDefaults}
    testID='add-note-modal'
  >
    {showContent && <View style={[Style.modalContent, styles.container]}>
      <Text style={[styles.title, noteViewMode && { alignSelf: 'flex-start' }]}>
        {noteViewMode ? note?.title : `${editNote ? 'Edit' : 'Add a'} note`}
      </Text>
      {Content()}
      <View style={[styles.buttonContainer, addNote && { justifyContent: 'center' }]}>
        {!addNote && <PressableStyled
          style={styles.deleteButton}
          onPress={attemptNoteDeletion}
          testID='delete-note-btn'
        >
          <BinIcon12 color={styles.deleteButton.borderColor} />
        </PressableStyled>}
        <View style={styles.buttonSubcontainer}>
          <Button
            title='Cancel'
            onPress={hide}
            style={styles.button}
            testID='cancel-adding-note-btn'
          />
          <Button
            affirmative
            title={buttonTitle}
            onPress={update}
            style={styles.button}
            {...{ disabled }}
            testID={editNote ? 'edit-note-btn' : 'save-note-btn'}
          />
        </View>
      </View>
      <PressableStyled
        onPress={hide}
        style={Style.closeButton}
        testID='close-note-modal-btn'
      >
        <CloseModalIcon />
      </PressableStyled>
      <ActivityIndicator
        size='large'
        style={Style.activityIndicator}
        animating={loading}
      />
    </View>}
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    width: 532,
    alignSelf: 'center',
    padding: 40,
    paddingTop: 60,
  },
  mobileContainer: {
    maxHeight: undefined,
  },
  title: {
    fontSize: 28,
    lineHeight: 42,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    alignSelf: 'center',
    marginBottom: 32,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 12,
  },
  signature: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  successMessage: {
    marginBottom: 60,
    marginTop: -40,
  },
  buttonContainer: {
    ...Style.row,
    marginTop: isMobile ? 28 : 40,
    marginBottom: isMobile ? 24 : 0,
  },
  buttonSubcontainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    paddingHorizontal: 24,
    height: 40,
    marginStart: undefined,
  },
  deleteButton: {
    height: isMobile ? 38 : '100%',
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksRed700,
    borderRadius: 4,
    shadowOpacity: 0.06,
    shadowRadius: 2,
    shadowOffset: { width: 2, height: 4 },
    shadowColor: '#323247',
    marginEnd: 8,
  },
  descriptionField: {
    minHeight: 116,
    alignContent: 'flex-start',
  },
  errorMessage: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
    marginTop: 8,
  },
});

export default UpdateNoteModal;
